import React, {Key, useEffect, useState} from 'react';
import {wdiCore, WdiModalAppProps, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, DatePicker, Input, message, Popover, TableColumnsType, Typography} from 'antd';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {FilterColumn, GridFilterForm} from '@/components/GridWidget/GridFilterForm';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {TablePaginationConfig} from "antd/lib/table";
import {SorterResult} from "antd/lib/table/interface";
import {SA} from "@/services/report/sa";
import {CloseOutlined} from '@ant-design/icons';
import {getActivityStatus, getSaLink} from "@/pages/sales/sa/components/data";
import {DataOptions} from "@/services/basic/metadata";
import {Enum} from "@/enums/system";
import {FormTypeEnum} from "@/enums/formType";

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any
};

type SaFilterParamProps = {
  statusIds?: number[],
  pageGroupIds?: FormTypeEnum.PageGroup[],
  paymentMethodIds?: Enum.PaymentMethod[],
}

export type ProjectSelectProps = {
  value?: API.FormSaVo[];
  onChange?: (value?: API.FormSaVo[]) => void;
  label?: string;
  filterParams?: SaFilterParamProps
};

export const SaSelectApp: React.FC<ProjectSelectProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const onClose = () => {
    setModalProps({visible: false, title: ""});
  };
  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: ""});
    if (result && result.code === ResponseStatus.SUCCESS) {
      props.onChange?.(result.data);
    }
  };

  const newProps: any = {
    onClick: () => {
      setModalProps({
        visible: true,
        title: "选择活动单",
        data: {
          records: props.value,
          filterParams: props.filterParams
        },
      });
    },
    allowClear: {
      clearIcon: (
        <Button
          shape='circle'
          style={{border: "none", width: 20, height: 20}}
          icon={<CloseOutlined/>}
          size="small"
          onClick={() => props.onChange?.([])}
        />
      )
    }
  };

  return (
    <>
      <Popover content={props.value?.map(item => item.formNo).join()}>
        <Input
          {...newProps}
          placeholder={"请选择活动单"}
          value={props.value?.map(item => item.formNo).join()}
        />
      </Popover>
      {
        modalProps.visible &&
        <FormSaSelectModalApp
          {...modalProps}
          onClose={onClose}
          onSave={onSave}
        />
      }
    </>
  );
}

const FormSaSelectModalApp: React.FC<WdiModalAppProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<API.FormSaVo[]>([]);
  const [selectedRowsState, setSelectedRowsState] = useState<{
    version: number,
    rows: Map<Key, API.FormSaVo>
  }>({version: 0, rows: new Map<Key, API.FormSaVo>()});
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
    },
    sorter: null,
    filterOptions: null,
  });

  useEffect(() => {
    (async () => {
      setTableColumns(await initTableColumns())
      fetchData(Object.assign({}, tableParams));
    })()
  }, []);

  useEffect(() => {
    const records: API.FormSaVo[] = props.data?.records ?? [];
    records.forEach((item) => {
      addSelectedRowState(item);
    })
  }, [props.data?.records])

  const addSelectedRowState = (row: API.FormSaVo) => {
    if (!row.formNo) return;
    const rowMap = selectedRowsState.rows;
    rowMap.set(row.formNo, row);
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }

  const delSelectedRowState = (row: API.FormSaVo) => {
    if (!row.formNo) return;
    const rowMap = selectedRowsState.rows;
    rowMap.delete(row.formNo);
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }

  const filterColumns: FilterColumn[] = [
    {
      title: '活动单号',
      dataIndex: 'formNo',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入活动单号"}/>;
      }
    },
    {
      title: '活动名称',
      dataIndex: 'activityName',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入活动名称"}/>;
      },
    },
    {
      title: '活动期间',
      dataIndex: 'activityDateRange',
      span: 8,
      render: () => {
        return <DatePicker.RangePicker style={{width: '100%'}}/>
      }
    },
  ];
  const initTableColumns = async () => {
    const formStatusRes = await DataOptions.search({typeGroup: 3, notInValues: '0'})
    const formStatusOptions = formStatusRes?.data ?? [];
    const columns: TableColumnsType<API.FormSaVo> = [
      {
        title: '活动单号',
        dataIndex: 'formNo',
        ellipsis: true,
        width: 150,
        render: (_, record) => (
          <Typography.Link onClick={() => {
            window.open(`${getSaLink(record)}?formId=${record.formSaId}&formTypeId=${record.formTypeId}&pageTypeId=${record.pageTypeId}`);
          }}>{record.formNo}</Typography.Link>
        )
      },
      {
        title: '活动名称',
        width: 200,
        dataIndex: 'activityName',
        ellipsis: true,
      },
      {
        title: '活动状态',
        width: 150,
        dataIndex: 'statusId',
        ellipsis: true,
        render: (_, record) => {
          return getActivityStatus(record, formStatusOptions)
        },
      },
      {
        title: '活动期间',
        dataIndex: 'activityDateRange',
        ellipsis: true,
        render: (_, record) => {
          return wdiCore.String.Join(record.activityStartDate, record.activityEndDate)
        },
      },
      {
        title: '申请金额',
        width: 150,
        dataIndex: 'amount',
        ellipsis: true,
        render: (value) => (
          <span>{wdiCore.Number.AmountToString(value)}</span>
        ),
      },
      {
        title: '申请人',
        width: 150,
        dataIndex: 'staffName',
        ellipsis: true,
      },
      {
        title: '申请日期',
        dataIndex: 'submitDate',
        ellipsis: true,
      },
    ];
    return columns
  }

  const getFilterOptions = (params: TableParams) => {
    const filterParams:SaFilterParamProps = props.data?.filterParams;
    const filterOptions: any[] = [];
    if (filterParams?.statusIds) {
      filterOptions.push({
        title: '活动状态',
        field: 'statusId',
        opt: 8,
        value: filterParams.statusIds
      })
    }
    if (filterParams?.pageGroupIds) {
      filterOptions.push({
        title: 'pageGroupId',
        field: 'pageGroupId',
        opt: 8,
        value: filterParams.pageGroupIds
      })
    }
    if(filterParams?.paymentMethodIds){
      filterOptions.push({
        title: 'paymentMethodId',
        field: 'paymentMethodId',
        opt: 8,
        value: filterParams.paymentMethodIds
      })
    }


    if (params.filterOptions) {
      const {formNo, activityName, activityDateRange} = params.filterOptions;
      if (formNo) {
        filterOptions.push({
          title: '活动单号',
          field: 'formNo',
          opt: 7,
          value: formNo
        })
      }
      if (activityName) {
        filterOptions.push({
          title: '活动名称',
          field: 'activityName',
          opt: 7,
          value: activityName
        })
      }
      if (activityDateRange?.[0]) {
        filterOptions.push({
          title: '活动期间',
          field: 'activityStartDate',
          opt: 4,
          value: activityDateRange[0]
        })
      }
      if (activityDateRange?.[1]) {
        filterOptions.push({
          title: '活动期间',
          field: 'activityStartDate',
          opt: 6,
          value: activityDateRange[1]
        })
      }
      wdiCore.Object.RemoveNullOrUndefined(filterOptions);
    }
    return filterOptions;
  }

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: any = {
      filterOptions: JSON.stringify(filterOptions),
      id: 'SelectActivity',
      pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
      sortdatafield: 'submitDate',
      sortorder: 'DESC'
    };
    SA.queryAll(req).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data.records);
        setTableParams({
          pagination: {
            ...params.pagination,
            total: data.totalCount,
          },
          sorter: params.sorter,
          filterOptions: params.filterOptions
        });
        setLoading(false);
      }
    });
  };
  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const onSelectChange = (record: API.FormSaVo, selected: boolean) => {
    if (!record.formNo) return;
    if (selected) {
      addSelectedRowState(record)
      return;
    }
    delSelectedRowState(record)
  };

  const onSelectAllChange = (selected: boolean, selectedRows: API.FormSaVo[], changeRows: API.FormSaVo[]) => {
    if (selected) {
      changeRows.forEach(item => addSelectedRowState(item))
      return;
    }
    changeRows.forEach(item => delSelectedRowState(item))
  };

  const doSave = () => {
    const rows = Array.from(selectedRowsState.rows.values());
    if (!rows || rows.length == 0) {
      message.error('请选择活动单');
      return false;
    }
    props.onSave?.({code: ResponseStatus.SUCCESS, data: rows});
    return true;
  };

  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      bodyStyle={{height: 600}}
      onCancel={() => {
        props.onClose?.()
      }}
      onOk={doSave}
    >
      <GridFilterForm doSearch={doSearch} columns={filterColumns}/>
      <WdiTable
        rowSelection={{
          selectedRowKeys: Array.from(selectedRowsState.rows.keys()),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange
        }}
        columns={tableColumns}
        showPageRows={10}
        rowKey="formNo"
        dataSource={dataSource}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(Object.assign({}, tableParams, {
              pagination: {
                ...tableParams.pagination,
                current: page,
                pageSize: pageSize
              }
            }))
          }
        }}
        loading={loading}
      />
    </WdiModal>
  );
};

